/**
 * Auto-genereer SEO-vriendelijke alt-text voor afbeeldingen op basis van
 * pagina-context (city + brand). Wordt door image-components aangeroepen
 * als fallback wanneer er geen expliciete alt-tekst is.
 *
 * Strategie: natuurlijke beschrijvende zin met de relevante keywords —
 * voorkomt keyword-stuffing, sluit aan op pagina-onderwerp.
 *
 * Voorbeelden voor /badkamer-renovatie-enschede:
 *   buildImageAlt('hero')                     -> "Moderne badkamerrenovatie in Enschede"
 *   buildImageAlt('product', {item:'Sunshower'}) -> "Sunshower in moderne badkamer Enschede"
 *   buildImageAlt('cadeau', {item:'Gratis meubelset'}) -> "Gratis meubelset bij badkamerrenovatie Enschede"
 *   buildImageAlt('before-after')             -> "Voorbeeld badkamerrenovatie Enschede - voor en na"
 *
 * Voorbeelden voor /inloopdouche (geen city):
 *   buildImageAlt('hero')                     -> "Moderne badkamer met inloopdouche"
 *   buildImageAlt('product', {item:'Inloopdouche'}) -> "Inloopdouche - badkameroplossingen"
 */

export interface AltContext {
  /** Plaatsnaam afgeleid van de pagina (DKI). Leeg op niet-city pagina's. */
  city?: string;
  /** Slug van de pagina (bv. 'badkamer-renovatie-enschede' of 'inloopdouche'). */
  pageSlug?: string;
  /** Display-name van de pagina (bv. 'Badkamer renovatie Enschede'). */
  pageName?: string;
  /** Naam van het specifieke item (productnaam, actie-label, etc.). */
  item?: string;
  /** Brand-name uit shared content. */
  brandName?: string;
}

export type AltPurpose =
  | "hero"
  | "product"
  | "cadeau"
  | "before-after"
  | "transformation-video"
  | "advisor"
  | "process-step"
  | "regio-map"
  | "brand"
  | "generic";

/** Capitalizeer de eerste letter (bv. "enschede" -> "Enschede"). */
function cap(s: string): string {
  if (!s) return s;
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** Detecteer of een slug een topic-keyword bevat dat in de alt past. */
function topicFromSlug(slug?: string): string | undefined {
  if (!slug) return undefined;
  const s = slug.toLowerCase();
  // "1-dag" varianten EERST checken (winnen van generieke matches):
  //   /badkamer-in-1-dag         -> "badkamer-1-dag"
  //   /inloopdouche-in-1-dag     -> "inloopdouche-1-dag"
  //   /bad-naar-douche-in-1-dag  -> "douche-1-dag"
  //   /douche-in-1-dag           -> "douche-1-dag"
  if (s.includes("1-dag") || s.includes("1dag") || s.includes("een-dag")) {
    if (s.includes("inloopdouche")) return "inloopdouche-1-dag";
    if (s.includes("douche")) return "douche-1-dag";
    return "badkamer-1-dag";
  }
  if (s.includes("inloopdouche")) return "inloopdouche";
  if (s.includes("bad-naar-douche") || s.includes("douche-renovatie")) return "douche-renovatie";
  if (s.includes("instapbad")) return "instapbad";
  if (s.includes("inloopbad")) return "inloopbad";
  if (s.includes("seniorenbadkamer") || s.includes("senioren")) return "seniorenbadkamer";
  if (s.includes("seniorendouche")) return "seniorendouche";
  if (s.includes("douchecabine")) return "douchecabine";
  if (s.includes("badkamermeubel")) return "badkamermeubel";
  if (s.includes("wandpanelen")) return "badkamer-wandpanelen";
  if (s.includes("renovatie") || s.includes("vernieuw") || s.includes("opknappen")) return "badkamerrenovatie";
  if (s.includes("kosten")) return "badkamer-kosten";
  if (s.includes("mobiliteit")) return "mobiliteit-badkamer";
  return "badkamer";
}

export function buildImageAlt(purpose: AltPurpose, ctx: AltContext = {}): string {
  const city = ctx.city ? cap(ctx.city.trim()) : undefined;
  const topic = topicFromSlug(ctx.pageSlug);
  const item = ctx.item?.trim();
  const cityTail = city ? ` in ${city}` : "";

  switch (purpose) {
    case "hero": {
      // Hero: hoofdvisual van de pagina — beschrijf de hele scene.
      if (topic === "badkamer-1-dag") return `Badkamer in 1 dag${cityTail}`;
      if (topic === "inloopdouche-1-dag") return `Inloopdouche in 1 dag${cityTail}`;
      if (topic === "douche-1-dag") return `Douche in 1 dag${cityTail}`;
      if (topic === "inloopdouche") return `Moderne inloopdouche${cityTail}`;
      if (topic === "instapbad") return `Veilig instapbad${cityTail}`;
      if (topic === "inloopbad") return `Inloopbad voor senioren${cityTail}`;
      if (topic === "seniorenbadkamer") return `Veilige seniorenbadkamer${cityTail}`;
      if (topic === "seniorendouche") return `Seniorvriendelijke douche${cityTail}`;
      if (topic === "douche-renovatie") return `Douche renovatie${cityTail}`;
      if (topic === "douchecabine") return `Moderne douchecabine${cityTail}`;
      if (topic === "badkamermeubel") return `Stijlvol badkamermeubel${cityTail}`;
      if (topic === "badkamer-wandpanelen") return `Badkamer wandpanelen${cityTail}`;
      if (topic === "badkamerrenovatie") return `Moderne badkamerrenovatie${cityTail}`;
      return `Moderne badkamer${cityTail}`;
    }

    case "product": {
      if (item) return city ? `${item} - badkamer in ${city}` : `${item} - badkameroplossingen`;
      return city ? `Badkamerproduct - ${city}` : "Badkamerproduct";
    }

    case "cadeau": {
      if (item) {
        return city
          ? `${item} bij badkamerrenovatie ${city}`
          : `${item} bij nieuwe badkamer`;
      }
      return city
        ? `Gratis cadeau bij badkamerrenovatie ${city}`
        : "Gratis cadeau bij nieuwe badkamer";
    }

    case "before-after":
      return city
        ? `Voorbeeld badkamerrenovatie ${city} - voor en na`
        : "Voorbeeld badkamerrenovatie - voor en na";

    case "transformation-video":
      return city
        ? `Video badkamerrenovatie ${city} - transformatie van oud naar nieuw`
        : "Video badkamerrenovatie - transformatie van oud naar nieuw";

    case "advisor":
      return ctx.brandName
        ? `Adviseur ${ctx.brandName}${cityTail ? ` in ${city}` : ""}`
        : `Persoonlijk adviseur${cityTail}`;

    case "process-step":
      return item ? `${item} - stap in proces` : `Proces-stap badkamerrenovatie${cityTail}`;

    case "regio-map":
      return city
        ? `Servicegebied badkamerrenovatie rondom ${city}`
        : "Servicegebied Nederland - badkamerrenovaties";

    case "brand":
      return item ? `${item} - merkpartner` : "Merkpartner";

    default:
      return city ? `Badkamer in ${city}` : "Badkamer";
  }
}
